import { z } from 'zod';
/**
 * The catalog: the definition of a *kind* of item (REQUIREMENTS §2.4).
 *
 * §3.1 is answered "placeholder for v0.9": the seed set is the working
 * catalog, and it is data — edited here by an admin or csm. Nothing
 * in code names an item, so the unit's real list loads later without a code
 * change.
 */
export const ownershipSchema = z.enum(['company', 'personal']);
export const trackingSchema = z.enum(['serialized', 'quantity']);
export const catalogUnitSchema = z.enum(['pcs', 'pairs', 'sets']);
export const catalogItemSchema = z.object({
    id: z.string().min(1),
    nameHe: z.string().min(1),
    nameEn: z.string().min(1),
    category: z.string().min(1),
    ownership: ownershipSchema,
    tracking: trackingSchema,
    /** A weapon-grade item: on the books at all times (§2.5). §3.2 answered "no approver", so this no longer gates transfers. */
    controlled: z.boolean(),
    /** Ammunition and the like: expended or written off, never returned. */
    consumable: z.boolean(),
    unit: catalogUnitSchema,
    requiresSignature: z.boolean(),
    returnable: z.boolean(),
    /**
     * May move soldier-to-soldier by a transfer (owner, 2026-09-13). Off means
     * the only way to another soldier is return and re-issue through the
     * storeroom — for everyone, admin included. Defaults on, so catalog items
     * written before the flag existed keep behaving as they did.
     */
    transferable: z.boolean().default(true),
    defaultDueDays: z.number().int().min(1).max(3650).nullable(),
    photoPath: z.string().nullable(),
    active: z.boolean(),
});
export const catalogResponseSchema = z.object({ items: z.array(catalogItemSchema) });
export const catalogItemResponseSchema = z.object({ item: catalogItemSchema });
/**
 * POST /api/catalog — a new kind of item. Admin or csm.
 *
 * Two combinations are refused as incoherent (the seed says why): a serialized
 * consumable, and a personal controlled item.
 */
export const createCatalogItemRequestSchema = catalogItemSchema
    .omit({ id: true, photoPath: true, active: true })
    .refine((i) => !(i.tracking === 'serialized' && i.consumable), {
    message: 'A serialized item cannot be consumable',
    path: ['consumable'],
})
    .refine((i) => !(i.ownership === 'personal' && i.controlled), {
    message: 'A personal item cannot be controlled',
    path: ['controlled'],
});
/**
 * PATCH /api/catalog/:id.
 *
 * `tracking` and `ownership` are not editable once units or assignments exist
 * against the item — that is a migration. Names, category, flags and defaults
 * are. A rename never rewrites history: signatures carry their own snapshot.
 */
export const updateCatalogItemRequestSchema = catalogItemSchema
    .omit({ id: true, tracking: true, ownership: true })
    .partial()
    .refine((b) => Object.keys(b).length > 0, { message: 'At least one field must be supplied' });
// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------
export const inventoryStatusSchema = z.enum([
    'in_stock',
    'issued',
    'in_rotation',
    'lost',
    'damaged',
    'retired',
]);
export const conditionSchema = z.enum(['good', 'needs_repair', 'unusable']);
/** One physical, serialized company item (§2.4 `inventoryUnits`). */
export const inventoryUnitSchema = z.object({
    id: z.string().min(1),
    catalogItemId: z.string().min(1),
    serialNumber: z.string().min(1),
    status: inventoryStatusSchema,
    currentHolderUid: z.string().nullable(),
    currentAssignmentId: z.string().nullable(),
    condition: conditionSchema,
    notes: z.string().nullable(),
    createdAt: z.string().nullable(),
});
/**
 * Stock counters for a quantity-tracked item (§2.4 `stockLevels`). Every
 * quantity item has one, company *and* personal (owner, 2026-09-13): the
 * storeroom counts the shirts on its shelf the same way it counts magazines,
 * and issuing either draws the count down.
 */
export const stockLevelSchema = z.object({
    catalogItemId: z.string().min(1),
    total: z.number().int().min(0),
    available: z.number().int().min(0),
    issued: z.number().int().min(0),
    updatedAt: z.string().nullable(),
});
/**
 * GET /api/inventory — the dashboard (§2.6): per catalog item, what exists
 * and where it is. Serialized items are counted from their units; quantity
 * items from their stock level.
 */
export const inventorySummarySchema = z.object({
    item: catalogItemSchema,
    total: z.number().int().min(0),
    available: z.number().int().min(0),
    issued: z.number().int().min(0),
    inRotation: z.number().int().min(0),
    lost: z.number().int().min(0),
    damaged: z.number().int().min(0),
    retired: z.number().int().min(0),
});
export const inventoryResponseSchema = z.object({ items: z.array(inventorySummarySchema) });
/** GET /api/inventory/units?catalogItemId=&serial=&status= — the serialized registry. */
export const inventoryUnitsQuerySchema = z.object({
    catalogItemId: z.string().min(1).optional(),
    /** Case-insensitive substring; exact match sorts first. */
    serial: z.string().trim().min(1).max(64).optional(),
    status: inventoryStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(200),
});
export const inventoryUnitsResponseSchema = z.object({
    units: z.array(inventoryUnitSchema),
    /** True when more units matched than were returned. */
    truncated: z.boolean(),
});
/**
 * POST /api/inventory/units — register serialized units. Admin or csm.
 * Serials are unique per catalog item; a batch with a duplicate is refused whole.
 */
export const addInventoryUnitsRequestSchema = z.object({
    catalogItemId: z.string().min(1),
    serialNumbers: z.array(z.string().trim().min(1).max(64)).min(1).max(200),
    condition: conditionSchema.default('good'),
    notes: z.string().max(500).nullable().default(null),
});
export const addInventoryUnitsResponseSchema = z.object({ units: z.array(inventoryUnitSchema) });
export const inventoryUnitResponseSchema = z.object({ unit: inventoryUnitSchema });
/**
 * POST /api/inventory/units/:id/damage — flag a unit sitting unheld in stock
 * as damaged (story 43). Refused if the unit is currently issued: that case
 * goes through the soldier-facing report flow on the assignment instead.
 */
export const markUnitDamagedRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** POST /api/inventory/units/:id/repair — the reverse: damaged, unheld stock back to good. */
export const repairUnitRequestSchema = z.object({
    notes: z.string().trim().max(500).nullable().default(null),
});
/**
 * POST /api/inventory/units/:id/write-off — retire a unheld unit (in_stock or
 * damaged) directly, with no assignment to write off through. Level-3 gear
 * reach, same as the assignment-based write-off.
 */
export const writeOffUnitRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/**
 * POST /api/inventory/units/:id/correct — fix a data-entry mistake on an
 * unheld unit (a mistyped serial, the wrong condition) directly, rather than
 * through a typed lifecycle action. Admin-only — tighter than the
 * storeroom-wide reach the other unit actions use, since this bypasses the
 * "no silent edits, only actions with a reason" principle everywhere else in
 * the app and should stay rare. At least one field must actually change.
 */
export const correctUnitRequestSchema = z
    .object({
    serialNumber: z.string().trim().min(1).max(64).optional(),
    condition: conditionSchema.optional(),
    notes: z.string().trim().max(500).nullable().optional(),
    reason: z.string().trim().min(3).max(500),
})
    .refine((b) => b.serialNumber !== undefined || b.condition !== undefined || b.notes !== undefined, {
    message: 'At least one of serialNumber, condition or notes must be supplied',
});
/**
 * POST /api/inventory/stock/:catalogItemId — adjust a quantity item's total,
 * company or personal. Admin or csm. Recorded as `stock_adjust` in the ledger with a
 * mandatory reason: a count that changed without one is a count nobody can
 * defend later.
 */
export const adjustStockRequestSchema = z.object({
    /** Positive to add stock, negative to remove. Cannot take `available` below zero. */
    delta: z.number().int().min(-100_000).max(100_000).refine((d) => d !== 0, {
        message: 'delta must be non-zero',
    }),
    reason: z.string().trim().min(3).max(500),
});
export const stockLevelResponseSchema = z.object({ stock: stockLevelSchema });
/**
 * POST /api/inventory/receive-kits — receive a form template's lines into
 * stock, `kits` times over (owner, 2026-09-13: "a personal kit: 5 magazines,
 * 1 vest, 2 shirts…" arrives as a count of kits, not as a list). One
 * transaction adds `line.quantity × kits` to every quantity-tracked line and
 * writes one `stock_adjust` ledger entry per item. Serialized lines are
 * skipped and reported back: a serial is registered one unit at a time.
 * Admin or csm.
 */
export const receiveKitsRequestSchema = z.object({
    templateId: z.string().min(1).max(128),
    kits: z.number().int().min(1).max(10_000),
    reason: z.string().trim().min(3).max(500),
});
export const receiveKitsResponseSchema = z.object({
    stocks: z.array(stockLevelSchema),
    /** Catalog item ids of serialized lines the template carried; nothing was received for them. */
    skippedSerialized: z.array(z.string().min(1)),
});
//# sourceMappingURL=catalog.js.map